import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { useEstablishmentCompanies } from "@/features/companyBranches/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/companyBranches/services/establishmentCompanyTransformer";
import { HandshakeIcon } from "lucide-react";
import { negotiationsListTableColumns } from "../../constants/negotiationsListTableColumns.constant";
import { useNegotiations } from "../../hooks/useNegotiations";
import { NegotiationsTransformer } from "../../services/NegotiationsTransformer";
import NegotiationsListEmptyState from "./EmptyState";
import { negotiationStatusOptions } from "../../constants/negotiationStatus.constant";

const NegotiationsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [searchEstablishment, setSearchEstablishment] = useDebounce('');

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
    },
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentCompanyTransformer.optionTransformer() }),
  });

  const {
    data: negotiations,
    meta,
    isFetching: negotiationsIsFetching,
    error: negotiationsError = {} as any,
    refetch: negotiationsRefetch,
  } = useNegotiations({
    filters: {
      page: filterParams.page || 1,
      status: filterParams.status?.value,
      establishment_id: filterParams.establishment_id?.value,
    },
    mapper: Mapper.mapWithTransformer({ transformer: NegotiationsTransformer.tableTransformer() }),
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader
          Icon={<HandshakeIcon className="w-10 h-10" strokeWidth={1.5} />}
          title="Negociações"
        />
      </div>

      <ErrorMessageWrapper
        className="max-h-[calc(100vh-128px)]"
        statusCode={negotiationsError?.status}
        refetchFunction={negotiationsRefetch}
      >
        {(meta.total === 0 && !Object.keys(filterParams)) && (
          <NegotiationsListEmptyState />
        )}

        {(meta.total > 0 || Object.keys(filterParams)) && (
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <ListFilters
                  filters={[
                    {
                      className: 'min-w-40',
                      onChange: (e: any) => setFilterParams((params) => ({ ...params, status: e?.value ? e : null })),
                      value: filterParams?.status || '',
                      options: [{ label: 'Todas', value: '' }, ...negotiationStatusOptions],
                      placeholder: 'Status',
                    },
                    {
                      className: 'min-w-40',
                      onChange: (e: any) => setFilterParams((params) => ({ ...params, establishment_id: e?.value ? e : null })),
                      onInputChange: setSearchEstablishment,
                      value: filterParams?.establishment_id || '',
                      options: [{ label: 'Todas', value: '' }, ...establishmentOptions],
                      placeholder: 'Estabelecimento',
                    },
                  ]}
                />
              </div>
            </div>

            <Table
              className="max-h-screen"
              columns={negotiationsListTableColumns}
              isLoadingData={negotiationsIsFetching}
              rows={negotiations}
              rowEmptyValue="--"
              page={filterParams.page || 1}
              setPage={(p) => setFilterParams((params) => ({ ...params, page: p }))}
              totalPages={meta.total_pages}
              totalCount={meta.total}
            />
          </div>
        )}
      </ErrorMessageWrapper>
    </div>
  );
};

export default NegotiationsList;
